import React, { useContext, useEffect, useState } from "react";
import DI from "./utility/DI";
import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListSubheader,
  Skeleton,
  TextField,
  Typography,
  Tabs,
  Tab,
  Chip,
  CircularProgress,
} from "@mui/material";
import PersonRemoveIcon from "@mui/icons-material/PersonRemove";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import HomeIcon from "@mui/icons-material/Home";
import GroupsIcon from "@mui/icons-material/Groups";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { toTitleCase } from "./utility/tools";
import { AppContext } from "../App";
import NoDataFound from "./EmptyFallback";
import { FetchContext } from "./panel/SideBar";
import { useNavigate, useSearchParams } from "react-router-dom";
import WalletPanel from "./panel/home/WalletPanel";

const iconBlank = <CheckBoxOutlineBlankIcon fontSize="small" />;
const iconChecked = <CheckBoxIcon fontSize="small" />;

const Manage = (props) => {
  const {
    di: { POST, GET, success, error, urls, session },
  } = props;

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { state, setState } = useContext(AppContext);
  const { refresh } = useContext(FetchContext);
  const [sectionTab, setSectionTab] = useState(() => {
    const section = searchParams.get("section");
    if (section === "groups") return 1;
    return 0; // default to homes
  });

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
        <IconButton onClick={() => navigate(-1)} sx={{ color: "text.primary" }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Manage
        </Typography>
      </Box>

      {/* Section Tabs — same style as original home tabs */}
      <Tabs
        value={sectionTab}
        onChange={(e, v) => setSectionTab(v)}
        variant="fullWidth"
        sx={{ mb: 1 }}
      >
        <Tab icon={<HomeIcon />} iconPosition="start" label="Homes" />
        <Tab icon={<GroupsIcon />} iconPosition="start" label="Shared Groups" />
      </Tabs>

      {sectionTab === 0 && (
        <HomeSection POST={POST} GET={GET} success={success} error={error} urls={urls} session={session} state={state} setState={setState} refresh={refresh} />
      )}
      {sectionTab === 1 && (
        <GroupSection POST={POST} GET={GET} success={success} error={error} urls={urls} session={session} refresh={refresh} />
      )}
    </Box>
  );
};

// =============================================
// HOMES — same layout as original AddHome.jsx
// =============================================
const HomeSection = ({ POST, GET, success, error, urls, session, state, setState, refresh }) => {
  const [loading, setLoading] = useState(true);
  const [homeName, setHomeName] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [list, setList] = useState([]);
  const [members, setMembers] = useState([]);
  const [currentHome, setCurrentHome] = useState(session?.homes?.[0]?.id ?? null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [homeToDelete, setHomeToDelete] = useState(null);

  const fetchUsers = async () => {
    try {
      setSearchLoading(true);
      const res = await POST(urls.get.users, { search: searchInput });
      if (res?.success) setList(res.data);
      else error(res?.message ?? "Error fetching users");
    } catch { error("Session expired. Refresh the page"); }
    finally { setSearchLoading(false); }
  };

  const addMember = async (id) => {
    if (!id || !currentHome) return error("User ID and Home ID cannot be null");
    try {
      setLoading(true);
      const res = await GET(urls.get.add_home_member, { user_id: id, home_id: currentHome });
      if (res?.success) { success(res?.message ?? "Member added"); fetchMembers(); }
      else error(res?.message ?? "Error adding member");
    } catch { error("Session expired"); }
    finally { setLoading(false); }
  };

  const removeMember = async (id) => {
    if (!id || !currentHome) return error("Member ID and Home ID cannot be null");
    try {
      setLoading(true);
      const res = await POST(urls.post.remove_home_members, { member_id: id, home_id: currentHome });
      if (res?.success) { success(res?.message ?? "Member removed"); fetchMembers(); }
      else error(res?.message ?? "Error removing member");
    } catch { error("Session expired"); }
    finally { setLoading(false); }
  };

  const fetchMembers = async () => {
    if (!currentHome) return;
    try {
      setLoading(true);
      const res = await GET(urls.get.get_home_members, { home_id: currentHome });
      if (res?.success) setMembers(res.data);
      else error(res?.message ?? "Error fetching members");
    } catch { error("Session expired"); }
    finally { setLoading(false); }
  };

  const getHomes = async () => {
    if (!session?.id) return;
    try {
      setLoading(true);
      const res = await GET(urls.get.get_homes, { user_id: session?.id });
      if (res?.success) {
        setState((prev) => ({ ...prev, session: { ...prev.session, homes: res?.homes ?? [] } }));
        setCurrentHome(res?.homes?.[0]?.id ?? null);
      } else error(res?.message ?? "Error fetching homes");
    } catch { error("Session expired"); }
    finally { setLoading(false); }
  };

  const handleAddHome = async () => {
    if (!homeName || !session?.id) return error("Home name is required");
    try {
      setLoading(true);
      const res = await POST(urls.get.create_home, { name: homeName, created_by: session?.id });
      if (res?.success) { success(res?.message ?? "Home added"); setHomeName(""); await getHomes(); fetchMembers(); }
      else error(res?.message ?? "Error adding home");
    } catch { error("Session expired"); }
    finally { setLoading(false); }
  };

  const handleDeleteHome = async () => {
    if (!homeToDelete || !session?.id) return;
    try {
      setLoading(true);
      const res = await POST(urls.get.delete_home, { home_id: homeToDelete.id, user_id: session?.id });
      if (res?.success) {
        success(res?.message ?? "Home deleted");
        setDeleteDialogOpen(false);
        setHomeToDelete(null);
        await getHomes();
        if (currentHome === homeToDelete.id) {
          const updatedHomes = session?.homes ?? [];
          setCurrentHome(updatedHomes.length > 0 ? updatedHomes[0]?.id : null);
        }
      } else error(res?.message ?? "Error deleting home");
    } catch { error("Session expired"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchMembers(); }, [currentHome]);
  useEffect(() => { getHomes(); }, [refresh]);
  useEffect(() => {
    const timeout = setTimeout(fetchUsers, 400);
    return () => clearTimeout(timeout);
  }, [searchInput]);

  return (
    <div>
      <TextField
        label="New home name?"
        value={homeName}
        onChange={(e) => setHomeName(e.target.value)}
        sx={{ width: "calc(100% - 20px)", margin: "10px", marginTop: "0px" }}
      />
      <Button
        variant="contained"
        color="primary"
        onClick={handleAddHome}
        disabled={loading || !homeName}
        sx={{ marginX: "10px" }}
      >
        Add Home
      </Button>

      <Tabs
        value={currentHome}
        onChange={(e, v) => setCurrentHome(v)}
        variant="scrollable"
        scrollButtons="auto"
        style={{
          display: "flex",
          justifyContent: "center",
          marginTop: "10px",
          marginBottom: "10px",
        }}
      >
        {session?.homes?.map(({ id, name }) => (
          <Tab
            key={id}
            icon={<HomeIcon />}
            iconPosition="start"
            label={name.toUpperCase()}
            value={id}
          />
        ))}
      </Tabs>

      {!session?.homes?.length ? <NoDataFound subMessage="Need to create home." /> : null}
      {currentHome != null && (
        <>
          {/* 1. Search friends / users input on top to add members */}
          <Autocomplete
            freeSolo
            id="home-member-search-autocomplete"
            loading={searchLoading}
            loadingText="Searching..."
            disableClearable
            filterOptions={(x) => x}
            inputValue={searchInput}
            onInputChange={(event, newInputValue, reason) => {
              if (reason === "input" || reason === "clear" || reason === "reset") {
                setSearchInput(newInputValue);
              }
            }}
            noOptionsText={searchInput.trim() ? "No match found" : "Type to search friends & users"}
            sx={{ margin: "10px", marginBottom: "0px" }}
            options={list}
            getOptionLabel={(option) =>
              typeof option === "string"
                ? option
                : `${option.id}# ${toTitleCase(option.firstname || "")} ${toTitleCase(option.lastname || "")} (${option.email || ""})`
            }
            isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search friends or users by ID, name, or email..."
                InputProps={{
                  ...params.InputProps,
                  type: "search",
                }}
              />
            )}
            renderOption={(props, userObj) => {
              const uId = String(userObj.id);
              const isAlreadyMember = members.some((m) => String(m.id) === uId);

              return (
                <Box
                  {...props}
                  key={uId}
                  sx={{ borderRadius: "8px", margin: "5px" }}
                  component="li"
                >
                  <div
                    onClick={() => {
                      if (uId && !isAlreadyMember) addMember(uId);
                    }}
                    style={{
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "4px 8px",
                      cursor: isAlreadyMember ? "default" : "pointer",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {toTitleCase(userObj?.firstname || "")} {toTitleCase(userObj?.lastname || "")}
                      </Typography>
                      <Typography variant="caption" sx={{ color: "text.secondary" }}>
                        #{uId}
                      </Typography>
                      {Boolean(Number(userObj?.is_friend)) && (
                        <Chip
                          label="Friend"
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{ height: 20, fontSize: "0.65rem", fontWeight: 700 }}
                        />
                      )}
                    </Box>

                    {isAlreadyMember ? (
                      <Chip label="Member" size="small" variant="filled" sx={{ height: 22, fontSize: "0.7rem" }} />
                    ) : (
                      <Button color="primary">
                        <PersonAddIcon />
                      </Button>
                    )}
                  </div>
                </Box>
              );
            }}
          />
          {list.length === 0 && searchInput.trim() !== "" && !searchLoading && (
            <small style={{ margin: "2px 12px", color: "grey" }}>No search results found.</small>
          )}

          {/* 2. Unified Member Wallets & Details Section with Remove member action */}
          <Box sx={{ px: "10px" }}>
            <WalletPanel
              currentHome={currentHome}
              embedded={true}
              onRemoveMember={removeMember}
              homeCreatorId={session?.homes?.find((h) => h.id === currentHome)?.created_by}
              refreshTrigger={members}
              key={"manage_wallet_" + currentHome + "_" + members.length}
            />
          </Box>

          {session?.id && currentHome && (
            <Box sx={{ padding: "16px", textAlign: "center", width: "100%" }}>
              <Button
                variant="contained"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => {
                  const home = session.homes.find((h) => h.id === currentHome);
                  if (home && home.created_by === session.id) {
                    setHomeToDelete({ id: home.id, name: home.name });
                    setDeleteDialogOpen(true);
                  }
                }}
                disabled={session?.homes?.some((home) => home.id === currentHome && home.created_by !== session.id)}
                fullWidth
                sx={{
                  backgroundColor: session?.homes?.some((home) => home.id === currentHome && home.created_by !== session.id)
                    ? "#cccccc"
                    : "#f44336",
                  "&:hover": {
                    backgroundColor: session?.homes?.some((home) => home.id === currentHome && home.created_by !== session.id)
                      ? "#cccccc"
                      : "#d32f2f",
                  },
                }}
              >
                Delete Home: "{session.homes.find((h) => h.id === currentHome)?.name}"
              </Button>
            </Box>
          )}
        </>
      )}

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete Home</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the home "<strong>{homeToDelete?.name}</strong>"?
            This action will permanently remove the home and all its associated expenses and members.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteHome} color="error" autoFocus disabled={loading}>
            {loading ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

// =============================================
// SHARED GROUPS — same pattern as homes
// =============================================
const GroupSection = ({ POST, GET, success, error, urls, session, refresh }) => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [friends, setFriends] = useState([]);
  const [currentGroup, setCurrentGroup] = useState(null);
  const [members, setMembers] = useState([]);

  // Create dialog state
  const [createOpen, setCreateOpen] = useState(false);
  const [newGroupName, setNewGroupName] = useState("");
  const [newGroupDesc, setNewGroupDesc] = useState("");
  const [newGroupMembers, setNewGroupMembers] = useState([]);
  const [createLoading, setCreateLoading] = useState(false);

  // Search friends for existing group
  const [searchInput, setSearchInput] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [friendList, setFriendList] = useState([]);

  const fetchGroups = async () => {
    setLoading(true);
    try {
      const res = await GET(urls.get.get_groups, { user_id: session?.id });
      if (res?.success) {
        const fetchedGroups = res.data || [];
        setGroups(fetchedGroups);
        if (fetchedGroups.length > 0 && !currentGroup) {
          setCurrentGroup(fetchedGroups[0].id);
        }
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const fetchFriends = async () => {
    const res = await POST(urls.get.get_friends);
    if (res?.success) setFriends(res.data || []);
  };

  const fetchFriendsSearch = async () => {
    try {
      setSearchLoading(true);
      const res = await POST(urls.get.users, { search: searchInput });
      if (res?.success) setFriendList(res.data || []);
    } finally { setSearchLoading(false); }
  };

  const fetchMembers = () => {
    const group = groups.find(g => g.id === currentGroup);
    setMembers(group?.members || []);
  };

  const handleCreateGroup = async () => {
    if (!newGroupName.trim()) return error("Group name is required");
    if (newGroupMembers.length === 0) return error("Add at least one member");
    setCreateLoading(true);
    try {
      const res = await POST(urls.post.create_group, {
        name: newGroupName.trim(),
        description: newGroupDesc.trim() || null,
        members: newGroupMembers.map((m) => m.id),
      });
      if (res?.success) {
        success("Group created!");
        setNewGroupName("");
        setNewGroupDesc("");
        setNewGroupMembers([]);
        setCreateOpen(false);
        fetchGroups();
      } else error(res?.message || "Failed to create group");
    } catch { error("Failed to create group"); }
    setCreateLoading(false);
  };

  const updateMember = async (userId, action) => {
    if (!currentGroup) return;
    setLoading(true);
    try {
      const res = await POST(urls.post.update_group_member, {
        group_id: currentGroup,
        user_id: userId,
        action: action
      });
      if (res?.success) {
        success(res.message);
        fetchGroups(); // Re-fetch to get updated member list in group object
      } else error(res.message);
    } catch { error("Failed to update member"); }
    setLoading(false);
  };

  const [deleteGroupDialogOpen, setDeleteGroupDialogOpen] = useState(false);
  const [deleteGroupLoading, setDeleteGroupLoading] = useState(false);

  const handleDeleteGroup = async () => {
    setDeleteGroupLoading(true);
    try {
      const res = await POST(urls.post.delete_group, { group_id: currentGroup });
      if (res?.success) {
        success("Group deleted");
        setDeleteGroupDialogOpen(false);
        setCurrentGroup(null);
        fetchGroups();
      } else {
        error(res?.message || "Failed to delete group");
      }
    } catch {
      error("Failed to delete group");
    }
    setDeleteGroupLoading(false);
  };

  useEffect(() => { fetchGroups(); fetchFriends(); }, [refresh]);
  useEffect(() => { fetchMembers(); }, [currentGroup, groups]);
  useEffect(() => {
    const timeout = setTimeout(fetchFriendsSearch, 400);
    return () => clearTimeout(timeout);
  }, [searchInput]);

  return (
    <div>
      <Button
        variant="contained"
        color="primary"
        fullWidth
        onClick={() => setCreateOpen(true)}
        sx={{ padding: "10px" }}
        startIcon={<AddIcon />}
      >
        Create New Group
      </Button>

      {groups.length > 0 && (
        <Tabs
          value={currentGroup}
          onChange={(e, v) => setCurrentGroup(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ mb: 1 }}
        >
          {groups.map((g) => (
            <Tab key={g.id} label={g.name.toUpperCase()} value={g.id} icon={<GroupsIcon />} iconPosition="start" />
          ))}
        </Tabs>
      )}

      {groups.length === 0 ? (
        <NoDataFound subMessage="Create a group to organize shared expenses." />
      ) : currentGroup && (
        <>
          <Autocomplete
            freeSolo
            id="group-member-search-autocomplete"
            loading={searchLoading}
            disableClearable
            filterOptions={(x) => x}
            inputValue={searchInput}
            onInputChange={(event, newInputValue, reason) => {
              if (reason === "input" || reason === "clear" || reason === "reset") {
                setSearchInput(newInputValue);
              }
            }}
            options={friendList}
            getOptionLabel={(option) =>
              typeof option === "string"
                ? option
                : `${option.id}# ${toTitleCase(option.firstname || "")} ${toTitleCase(option.lastname || "")}`
            }
            isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
            sx={{ margin: "10px" }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search member to add..."
              />
            )}
            renderOption={(props, userObj) => {
              const uId = String(userObj.id);
              const isAlreadyInGroup = members.some(m => String(m.id) === uId);

              return (
                <Box {...props} key={uId} component="li" sx={{ borderRadius: "8px", margin: "5px" }}>
                  <div
                    onClick={() => {
                      if (uId && !isAlreadyInGroup) updateMember(uId, 'add');
                    }}
                    style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center", padding: "4px 8px" }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {toTitleCase(userObj?.firstname || "")} {toTitleCase(userObj?.lastname || "")}
                      </Typography>
                      <Typography variant="caption" sx={{ color: "text.secondary" }}>
                        #{uId}
                      </Typography>
                      {Boolean(Number(userObj?.is_friend)) && (
                        <Chip label="Friend" size="small" color="primary" variant="outlined" sx={{ height: 20, fontSize: "0.65rem" }} />
                      )}
                    </Box>
                    {!isAlreadyInGroup ? <PersonAddIcon color="primary" /> : <Chip label="Member" size="small" />}
                  </div>
                </Box>
              );
            }}
          />

          <List
            subheader={<ListSubheader sx={{ fontSize: "18px", fontWeight: 600 }}>Group Members ({members.length})</ListSubheader>}
          >
            {members.map((member) => (
              <React.Fragment key={member.id}>
                <ListItem
                  secondaryAction={
                    groups.find(g => g.id === currentGroup)?.admin_id == session.id && member.id != session.id ? (
                      <IconButton edge="end" onClick={() => updateMember(member.id, 'remove')}>
                        <PersonRemoveIcon color="error" />
                      </IconButton>
                    ) : null
                  }
                >
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: "#7b1fa2" }}>{(member.firstname?.[0] || "?").toUpperCase()}</Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={`${toTitleCase(member.firstname || "")} ${toTitleCase(member.lastname || "")}`}
                    secondary={`${member.email || ""} ${groups.find(g => g.id === currentGroup)?.admin_id == member.id ? " (Admin)" : ""}`}
                  />
                </ListItem>
                <Divider variant="inset" component="li" />
              </React.Fragment>
            ))}
          </List>

          {groups.find(g => g.id === currentGroup)?.admin_id == session.id && (
            <Box sx={{ p: 2 }}>
              <Button fullWidth variant="contained" color="error" startIcon={<DeleteIcon />} onClick={() => setDeleteGroupDialogOpen(true)}>
                Delete Group: "{groups.find(g => g.id === currentGroup)?.name}"
              </Button>
            </Box>
          )}
        </>
      )}

      {/* Delete Group Confirmation Dialog */}
      <Dialog open={deleteGroupDialogOpen} onClose={() => setDeleteGroupDialogOpen(false)}>
        <DialogTitle>Confirm Delete Group</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the shared group "<strong>{groups.find(g => g.id === currentGroup)?.name}</strong>"?
            Transactions will be preserved but unlinked from this group.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteGroupDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteGroup} color="error" autoFocus disabled={deleteGroupLoading}>
            {deleteGroupLoading ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Group Dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Shared Group</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <TextField
            autoFocus
            label="Group Name"
            fullWidth
            sx={{ mb: 2, mt: 1 }}
            value={newGroupName}
            onChange={(e) => setNewGroupName(e.target.value)}
          />
          <TextField
            label="Description (Optional)"
            fullWidth
            sx={{ mb: 2 }}
            value={newGroupDesc}
            onChange={(e) => setNewGroupDesc(e.target.value)}
          />
          <Autocomplete
            multiple
            options={friends}
            getOptionLabel={(o) => o.firstname}
            value={newGroupMembers}
            onChange={(e, v) => setNewGroupMembers(v)}
            renderInput={(params) => <TextField {...params} label="Initial Members" />}
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox icon={iconBlank} checkedIcon={iconChecked} checked={selected} style={{ marginRight: 8 }} />
                {option.firstname}
              </li>
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateGroup} disabled={createLoading}>
            {createLoading ? <CircularProgress size={24} /> : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default DI(Manage);
